import { useEffect, useMemo, useState } from "react";

import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";
import { useDebounce } from "@uidotdev/usehooks";

import { AccountCreateEditModal } from "../../components/accounts/AccountCreateEditModal";
import AccountTable from "../../components/accounts/AccountsTable";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useContacts from "../../data/useContacts";
import { IContact } from "../../models/Contact";

export default function AccountList() {
  const { setInfo } = usePageNameContext();
  const [includeEbay, setIncludeEbay] = useState<boolean>(false);
  const [hideAuto, setHideAuto] = useState<boolean>(false);
  const { contacts } = useContacts(includeEbay, hideAuto);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredContacts, setFilteredContacts] = useState<IContact[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  const allContacts = useMemo(
    () =>
      contacts.data?.data
        .filter((contact) => contact.account)
      // .map((contact) => {
      //   const account = contact.accountId ? accountLookup(contact.accountId) ?? null : null;
      //   return {
      //     ...contact,
      //     account,
      //   } as IContact;
      // }
      ?? [],
    [contacts.data?.data, contacts.isLoading]
  );

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const search = (contactAccount: IContact, searchTerm: string) => {
    let st = searchTerm.toLowerCase();

    return (
      contactAccount.fullname?.toLowerCase().includes(st) ||
      contactAccount.account?.name?.toLowerCase().includes(st) ||
      contactAccount.email?.toLowerCase().includes(st) ||
      String(contactAccount.account?.accountNumber).includes(st)
    );
  };

  useEffect(() => {
    let tempContacts: IContact[] = [];

    if (!debouncedSearchTerm || debouncedSearchTerm === "") {
      tempContacts = allContacts;
    } else {
      tempContacts = allContacts.filter((ca) => search(ca, debouncedSearchTerm));
    }

    setFilteredContacts(tempContacts);
  }, [debouncedSearchTerm, allContacts]);

  useEffect(() => {
    setInfo({
      name: "Accounts",
      desc: "View and manage all accounts here",
    });
  }, []);

  return contacts.isLoading ? (
    <div className="mt-8 min-h-[50vh] my-4 p-4 bg-white rounded-lg shadow">
      <LoadingWheel />
    </div>
  ) : !contacts.data || !contacts.data.data || contacts.isError ? (
    <>
      <p className="text-lg text-red-600 font-medium">
        An error occurred retrieving accounts
      </p>
    </>
  ) : (
    <>
      <AccountCreateEditModal open={addOpen} setOpen={setAddOpen} />
      <div className="">
        <div className="mt-10 relative flex items-stretch justify-between flex-grow focus-within:z-10">
          <div className="flex items-center space-x-4">

            <div>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-96 pl-10 sm:text-sm"
                placeholder="Contact/Account Name, Email or Account No"
              />
            </div>

            <div className="flex gap-x-1 items-center">
              <label className="text-sm font-medium text-gray-500">
                Include eBay Accounts
              </label>
              <input
                onChange={(e) => setIncludeEbay(e.target.checked)}
                checked={includeEbay}
                type="checkbox"
              />
            </div>

            <div className="flex gap-x-1 items-center">
              <label className="text-sm font-medium text-gray-500">
                Show Only Regulars Accounts
              </label>
              <input
                onChange={(e) => setHideAuto(e.target.checked)}
                checked={hideAuto}
                type="checkbox"
              />
            </div>
          </div>

          <ButtonInput
            label="Add Account"
            classes=""
            isSubmit={false}
            onClick={() => setAddOpen(true)}
          />

        </div>

        <AccountTable data={filteredContacts} />
      </div>
    </>
  );
}